/**
 * A LevelRecipe is the deterministic plan the director produces from a LevelIdentity.
 * The generator's only job is to satisfy it; nothing outside the director decides
 * difficulty, size or shape.
 */

export const DIFFICULTY_BANDS = ['tutorial', 'gentle', 'easy', 'medium', 'hard', 'expert'] as const;
export type DifficultyBand = (typeof DIFFICULTY_BANDS)[number];

export const DIFFICULTY_BAND_LABELS: Readonly<Record<DifficultyBand, string>> = {
  tutorial: 'Warm-up',
  gentle: 'Gentle',
  easy: 'Easy',
  medium: 'Steady',
  hard: 'Tricky',
  expert: 'Testing',
};

export const PUZZLE_ARCHETYPES = [
  'broad-branching',
  'narrow-chain',
  'island-regions',
  'dense-center',
  'sparse-center',
  'corridor',
  'perimeter',
  'near-symmetric',
  'asymmetric',
  'silhouette-showcase',
] as const;
export type PuzzleArchetype = (typeof PUZZLE_ARCHETYPES)[number];

export const PLACEMENT_STRATEGIES = [
  'uniform',
  'frontier',
  'center-out',
  'perimeter-in',
  'corridor',
  'component-aware',
  'depth-biased',
  'near-symmetric',
  'sparse-islands',
] as const;
export type PlacementStrategyId = (typeof PLACEMENT_STRATEGIES)[number];

/** Integer weights consumed by the direction heuristic. */
export interface DirectionHeuristicWeights {
  readonly base: number;
  readonly blocksExisting: number;
  readonly createsDependency: number;
  readonly edgeDistance: number;
  readonly directionBalance: number;
  readonly localRepetition: number;
  readonly towardsInterior: number;
}

export type StencilRotation = 0 | 90 | 180 | 270;

export interface LevelRecipe {
  readonly gridWidth: number;
  readonly gridHeight: number;

  readonly stencilId: string;
  readonly stencilCategory: string;
  readonly rotation: StencilRotation;
  readonly mirrorHorizontal: boolean;
  readonly mirrorVertical: boolean;

  readonly targetArrowCount: number;
  readonly targetOccupancyPermille: number;

  readonly difficultyBand: DifficultyBand;
  readonly archetype: PuzzleArchetype;

  readonly targetDependencyDepth: number;
  readonly initialMoveRange: readonly [number, number];
  readonly averageMoveRange: readonly [number, number];
  readonly targetForcedStepRatioPermille: number;

  readonly placementStrategy: PlacementStrategyId;
  readonly directionHeuristics: DirectionHeuristicWeights;
  /** Inclusive range of path lengths, in cells, for arrows on this board. */
  readonly arrowLengthRange: readonly [number, number];
  /**
   * How strongly placement is drawn to cells inside the exit corridors of arrows already on
   * the board. Laying a path across another arrow's corridor is what blocks it, so this is
   * the main lever on dependency depth: 0 scatters independent arrows, high values build
   * long chains.
   */
  readonly blockingBias: number;
  /** Relative weight for a tail continuing straight ahead. */
  readonly straightBias: number;
  /** Relative weight for a tail turning a corner; higher values produce mazier arrows. */
  readonly turnBias: number;

  readonly candidateBudget: number;
  readonly maxPlacementAttempts: number;
  readonly maxRepairIterations: number;
  readonly allowFallback: boolean;

  /** Compact signature used to detect near-duplicate levels in a neighbourhood. */
  readonly signature: string;
}
